import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SwalService, TranslateService } from '__services';
import { CommonService } from '~/app/core/services';


@Component({
  selector: 'app-upload-file',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputFileComponent,
      multi: true
    }
  ],
  templateUrl: './input-file.component.html',
  styleUrls: ['./input-file.component.scss'],

})
export class InputFileComponent implements OnInit, ControlValueAccessor  {
  @ViewChild('attachment') attachment: ElementRef;
  @Input() fileAccept: string;
  @Input() sizeLimit: number | string;
  @Input() placeholder: string;
  @Input() classDrop: string = 'drop-file';
  @Input() disabled = false;
  @Input() editable = false;
  @Input() drop = false;
  @Input() hideInput = false;
  @Input() isRequired = false;
  @Input() fileInputMessage = '';
  @Input() cssClass: any = '';
  @Input() clickDrop: boolean = false;
  @Output() inputFileChanged = new EventEmitter<object>();

  onChange: (file: File) => void;

  file: File | null = null;
  invalidFile: boolean;
  maxSize: string;
  fileName: string;
  fileExtension: string;
  fileChanged: boolean;
  safetyDataSheet = '';
  fileReader = new FileReader();

  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList): void {
    this.onFileChanged(event);
  }

  ngOnInit(): void {
    const elInput = document.getElementById('fakeInput');
    if(elInput) {
      elInput.setAttribute('contenteditable', this.editable.toString());
    }
    this.onFileReader();
  }

  writeValue(value: any): void {
    if ( typeof(value) === 'string') {
      this.safetyDataSheet = value;
      this.fileName = value.split('/').slice(-1)[0];
    } else {
      this.resetFileInput();
    }
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => void ): void {
  }

  onFileChanged(event: FileList): void {
    this.file = event && event.item(0);
    if (this.file) {
      this.fileName = this.file.name;
      this.fileExtension = this.file.name.split('.').pop();
      this.fileChanged = true;
      this.fileReader.readAsArrayBuffer(this.file);
    }
  }

  onFileReader() {
    this.fileReader.onloadend = (e: any) => {
      let fileEmit = this.file;
      const unit8 = new Uint8Array(e.target.result);
      const arr = unit8.subarray(0, 4);
      let header = '';
      for (const iterator of arr) {
        header += iterator.toString(16);
      }
      const fileType = this.validateFile(header);
      this.invalidFile = !this.validateFileType(fileType);

      if (this.sizeLimit) {
        this.maxSize = this.validateFileSize();
      }

      // if(fileType === 'pdf' && this.isEncryptedPdf(unit8)) {
      //   this.invalidFile = true;
      //   SwalService.warning(this.translateService.translate('Uploaded file was encrypted, please upload different file'))
      // }

      if (this.invalidFile || this.maxSize) {
        let msgError = ''
        if(this.invalidFile) {
          msgError = 'Invalid file type. Please upload a PDF, DOC, DOCX, XLS, XLSX, or image (excluding GIF).'
        }
        this.resetFileInput();
        fileEmit = null;
        SwalService.warning(this.translateService.translate(msgError));
      }
      this.onChange(fileEmit);
      this.inputFileChanged.emit({file: this.file, newName: ''});
    };
  }

  onFileDropped(value): void {
    this.onFileChanged(value);
  }

  onViewDocument(): void {
    this.commonService.processFile(this.safetyDataSheet);
  }

  onBlur(value: any): void {
    if (this.file?.name === value.target.textContent) {
      return;
    }
    // this.inputFileChanged.emit({file: this.file, newName: value.target.textContent});
  }

  onClickDrop() {
    if(this.clickDrop) {
      this.attachment.nativeElement.click();
    }
  }

  public resetFileInput(): void {
    this.host.nativeElement.value = '';
    this.file = null;
    this.fileName = '';
    if(this.attachment) {
      this.attachment.nativeElement.value = '';
    }
  }

  public validateFile(header): string | string[] {
    // Check the file signature against known types
    console.log(header);
    
    let type: string | string[] = 'unknown';
    switch (header) {
      case '89504e47': // image/png
        type = 'image/png';
        break;
      case '3c737667': // image/svg+xml
        type = 'image/svg';
        break;
      case '47494638': // image/gif
        type = 'image/gif';
        break;
      case 'ffd8ffe0':
        type = 'image/jpg';
        break;
      case 'ffd8ffe1':
      case 'ffd8ffe2':
      case 'ffd8ffe3':
      case 'ffd8ffee':
      case 'ffd8ffdb':
        type = 'image/jpeg';
        break;
      case 'd0cf11e0': // doc, application/vnd.ms-excel, ppt
        type = ['doc', 'xls'];
      case '504b0304': // docx, xlsx, pptx.
      case '504b34':
        // application/vnd.openxmlformats-officedocument.wordprocessingml.document
        // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
        // pplication/vnd.openxmlformats-officedocument.presentationml.presentation
        type = ['docx', 'xlsx'];
        break;
      case '25504446':
        // application/pdf
        type = 'pdf';
        break;
    }
    return type;
  }

  public validateFileType(fileType: string | string[]): boolean {
    let isValid = false;
    console.log(fileType);
    
    if(typeof(fileType) === 'string') {
      isValid = this.fileAccept.includes(fileType);
    } else if(fileType.length) {
      isValid = fileType.some(item => this.fileAccept.includes(item))
      if(isValid) {
        isValid = this.fileAccept.includes(this.fileExtension);
      }
    }
    return isValid
  }

  public validateFileSize(): string {
    const fileSzie = this.file.size / 1024 / 1024;
    return fileSzie > Number(this.sizeLimit) ? this.translateService.translate('File size exceeds {{sizeLimit}} MB.')
      .replace('{{sizeLimit}}', this.sizeLimit.toString()) : '';
  }

  public isEncryptedPdf(bytes: Uint8Array): boolean {
    const encryptionDictionary = [0x2F, 0x45, 0x6E, 0x63, 0x72, 0x79, 0x70, 0x74];
    for (let i = 0; i < bytes.length - encryptionDictionary.length; i++) {
      let match = true;
      for (let j = 0; j < encryptionDictionary.length; j++) {
        if (bytes[i + j] !== encryptionDictionary[j]) {
          match = false;
          break;
        }
      }
      if (match) {
        return true;
      }
    }
    return false;
  }

  // checkPDFEncryption(file: File) {
  //   const fileReader = new FileReader();

  //   fileReader.onload = (event) => {
  //     const typedArray = new Uint8Array(event.target.result as ArrayBuffer);

  //     pdfjsLib.getDocument(typedArray).promise.then((pdf) => {
  //       pdf.getMetadata().then((metadata) => {
  //         console.log(metadata);

          // if (metadata.encrypted) {
          //   console.log('The PDF file is encrypted.');
          // } else {
          //   console.log('The PDF file is not encrypted.');
          // }
  //       });
  //     })
  //   };

  //   fileReader.readAsArrayBuffer(file);
  // }


  constructor(
    private host: ElementRef<HTMLInputElement>,
    private commonService: CommonService,
    private translateService: TranslateService) { }
}
